/**
 * Shared constants/types for the browser → server client-log pipeline.
 *
 * Kept dependency-free and free of any `window`/`process` access so it is safe
 * to import from the Edge runtime (middleware), the Node runtime (the ingest
 * route handler), and client components alike.
 */

/** Endpoint the browser posts batches to; also added to middleware's public paths. */
export const CLIENT_LOG_ENDPOINT = "/api/client-logs";

/** Max log entries accepted per POST (and the client's flush threshold). */
export const MAX_CLIENT_LOG_BATCH = 50;

/** Per-message character cap — guards Loki/stdout against a flooding client. */
export const MAX_CLIENT_LOG_MSG_CHARS = 2000;

/** Cap on the JSON-serialized `context` object per entry. */
export const MAX_CLIENT_LOG_CONTEXT_CHARS = 4000;

/** String levels, mirroring the server logger so Loki level detection matches. */
export const CLIENT_LOG_LEVELS = ["debug", "info", "warn", "error"] as const;

export type ClientLogLevel = (typeof CLIENT_LOG_LEVELS)[number];

export interface ClientLogEntry {
  level: ClientLogLevel;
  /** Human-readable message. */
  msg: string;
  /** ISO-8601 timestamp from the client clock (advisory; server stamps its own). */
  time?: string;
  /** Arbitrary structured context (stack, component, url, …). */
  context?: Record<string, unknown>;
}

export interface ClientLogPayload {
  logs: ClientLogEntry[];
}
